import { ChangeDetectionStrategy, Component, effect, inject, model, output, signal, untracked } from '@angular/core';
import moment from 'moment';
import { PlatformIChingImportResultDto } from '@data/_generated-api/platform-api/models/platform-i-ching-import-result-dto';
import { SharedModule } from '@shared/shared.module';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HdContentLanguageService } from '../../../chart/bodygraph/hd-content-language.service';
import { injectIChingTranslate } from '../../consult/iching-consult-translate';
import { HdIChingMomentsService } from '../../moment/hd-iching-moments.service';
import { HdIChingJournalExportService } from './hd-iching-journal-export.service';
import { IChingJournalParseResult, parseJournalFile } from './iching-journal-file';

type ParsedFile = IChingJournalParseResult;
type ImportState = 'pick' | 'busy' | 'done';

/**
 * Dagboek importeren (spec 2026-10-21-dagboek-momenten-export-design §4): bestand kiezen, eerst
 * een samenvatting met uitleg over dubbels en chartkoppeling, dan importeren en de uitkomst tonen.
 * Na een geslaagde import meldt `imported` de uitkomst (de host herlaadt zijn lijst) en hoort de
 * tijdlijn het via de I Tjing-momentenservice.
 */
@Component({
  selector: 'hd-iching-import-dialog',
  imports: [SharedModule],
  templateUrl: './hd-iching-import-dialog.component.html',
  styleUrl: './hd-iching-import-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdIChingImportDialogComponent {
  readonly open = model(false);
  readonly imported = output<PlatformIChingImportResultDto>();

  private readonly exporter = inject(HdIChingJournalExportService);
  private readonly moments = inject(HdIChingMomentsService);
  private readonly contentLang = inject(HdContentLanguageService);
  protected readonly t = injectIChingTranslate();

  protected readonly state = signal<ImportState>('pick');
  protected readonly fileName = signal('');
  protected readonly parsed = signal<ParsedFile | undefined>(undefined);
  /** i18n-sleutel van de fout (bestand of server). */
  protected readonly errorKey = signal<string | undefined>(undefined);
  protected readonly result = signal<PlatformIChingImportResultDto | undefined>(undefined);

  constructor() {
    // Elke keer openen begint met een lege keuze.
    effect(() => {
      if (!this.open()) return;
      untracked(() => this.reset());
    });
  }

  protected onOpenChange(open: boolean): void {
    if (!open && this.state() === 'busy') return;
    this.open.set(open);
  }

  protected async onFileChosen(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.errorKey.set(undefined);
    this.parsed.set(undefined);
    this.fileName.set(file.name);
    let text: string;
    try {
      text = await file.text();
    } catch {
      this.errorKey.set('read');
      return;
    }
    const parsed = parseJournalFile(text);
    if (!parsed.ok) {
      this.errorKey.set(parsed.error ?? 'wrongFormat');
      return;
    }
    if (parsed.items.length === 0) {
      this.errorKey.set('empty');
      return;
    }
    this.parsed.set(parsed);
  }

  protected async start(): Promise<void> {
    const parsed = this.parsed();
    if (!parsed || this.state() === 'busy') return;
    this.state.set('busy');
    this.errorKey.set(undefined);
    try {
      const result = await this.exporter.importItems(parsed.items);
      this.result.set(result);
      this.state.set('done');
      if ((result.imported ?? 0) > 0) this.moments.markChanged();
      this.imported.emit(result);
    } catch {
      this.state.set('pick');
      this.errorKey.set('server');
    }
  }

  protected close(): void {
    this.open.set(false);
  }

  protected foundText(parsed: ParsedFile): string {
    const locale = momentLocaleFor(this.contentLang.effectiveLang());
    const from = moment(parsed.firstAt).locale(locale).format('D MMMM YYYY');
    const to = moment(parsed.lastAt).locale(locale).format('D MMMM YYYY');
    const count = parsed.items.length;
    if (from === to) return this.t('humanDesign.iching.export.import.foundOneDay', { count, date: from });
    return this.t('humanDesign.iching.export.import.found', { count, from, to });
  }

  private reset(): void {
    this.state.set('pick');
    this.fileName.set('');
    this.parsed.set(undefined);
    this.errorKey.set(undefined);
    this.result.set(undefined);
  }
}
